import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { Section, SectionHeader, SectionItem, SEO } from '@/components';
import { pricingTiers, pricingFaqs, comparisonFeatures } from '@/data/pricing';
import clsx from 'clsx';
import { trackPricingSelection, trackCTAClick, trackConversion } from '@/utils/analytics';

// Generate FAQ structured data for SEO
const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: pricingFaqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
};

export function PricingPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [isAnnual, setIsAnnual] = useState(true);

  // Calculate discounted price for annual billing (20% off)
  const getDisplayPrice = (price: number | 'contact') => {
    if (price === 'contact') return 'contact';
    return isAnnual ? Math.round(price * 0.8) : price;
  };

  return (
    <>
      <SEO
        title="Pricing - Affordable Plans for Every Service Business"
        description="Simple, transparent pricing for service companies of all sizes. Start with a 14-day free trial. No credit card required. All plans include implementation support."
        keywords={[
          'field service software pricing',
          'service business software cost',
          'HVAC software pricing',
          'plumbing software cost',
          'dispatch software pricing',
          'small business software',
        ]}
        ogImage="/og/pricing.png"
        type="website"
      />
      {/* FAQ Schema for rich snippets */}
      <script type="application/ld+json">
        {JSON.stringify(faqSchema)}
      </script>

      {/* Hero */}
      <section className="bg-ink pt-32 pb-20">
        <div className="container-marketing">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="font-display text-h1 text-paper mb-6">
              Simple pricing that scales with you
            </h1>
            <p className="text-xl text-gray-300 mb-4">
              The complete Business Operating System for trades—FSM, CRM, EOS, LMS, and AI—all in one platform.
            </p>
            <p className="text-gray-400 mb-8">
              60-70% less than buying these tools separately.
            </p>

            {/* Annual/Monthly Toggle */}
            <div className="flex items-center justify-center gap-4">
              <span className={clsx('text-sm font-medium', !isAnnual ? 'text-white' : 'text-gray-400')}>
                Monthly
              </span>
              <button
                onClick={() => setIsAnnual(!isAnnual)}
                className={clsx(
                  'relative w-14 h-7 rounded-full transition-colors',
                  isAnnual ? 'bg-accent' : 'bg-gray-600'
                )}
                aria-label="Toggle annual billing"
              >
                <motion.div
                  className="absolute top-1 left-1 w-5 h-5 bg-white rounded-full shadow"
                  animate={{ x: isAnnual ? 28 : 0 }}
                  transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                />
              </button>
              <span className={clsx('text-sm font-medium', isAnnual ? 'text-white' : 'text-gray-400')}>
                Annual
              </span>
              {isAnnual && (
                <motion.span
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-success text-white text-xs font-bold px-2 py-1 rounded-full"
                >
                  Save 20%
                </motion.span>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <Section className="-mt-8">
        <div className="text-center mb-6">
          <p className="text-muted text-sm">
            All plans include 5 technicians. Additional technicians: <span className="font-semibold">$59/month each</span>
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {pricingTiers.map((tier) => (
            <SectionItem key={tier.id}>
              <PricingCardEnhanced
                name={tier.name}
                description={tier.description}
                price={getDisplayPrice(tier.price)}
                originalPrice={tier.price}
                priceUnit={tier.priceUnit}
                features={tier.features}
                ctaLabel={tier.cta.label}
                ctaHref={tier.cta.href}
                highlighted={tier.highlighted}
                isAnnual={isAnnual}
                className="h-full"
              />
            </SectionItem>
          ))}
        </div>

        {/* Money-back Guarantee Badge */}
        <SectionItem>
          <motion.div
            className="mt-12 flex justify-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center gap-3 bg-cream border border-gray-200 rounded-full px-6 py-3">
              <ShieldCheckIcon className="w-6 h-6 text-success" />
              <div className="text-sm">
                <span className="font-semibold">30-day money-back guarantee</span>
                <span className="text-muted ml-1">- No questions asked</span>
              </div>
            </div>
          </motion.div>
        </SectionItem>
      </Section>

      {/* Feature Comparison */}
      <Section background="cream">
        <SectionHeader
          title="Compare plans"
          subtitle="Find the right fit for your business"
        />
        <SectionItem>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px]">
              <thead>
                <tr className="border-b-2 border-gray-300">
                  <th className="text-left py-4 px-4 font-heading font-semibold w-1/4">Feature</th>
                  {pricingTiers.map((tier) => (
                    <th key={tier.id} className="text-center py-4 px-2 font-heading font-semibold">
                      <div className="flex flex-col items-center gap-1">
                        <span>{tier.name}</span>
                        {tier.price !== 'contact' && (
                          <span className="text-xs text-muted font-normal">${tier.price}/mo</span>
                        )}
                        {tier.highlighted && (
                          <span className="text-xs bg-accent text-white px-2 py-0.5 rounded-full font-medium">
                            Most Popular
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {comparisonFeatures.map((feature, i) => (
                  <tr key={i} className={clsx('border-b border-gray-100', feature.category && 'bg-gray-50')}>
                    <td className={clsx('py-3 px-4', feature.category && 'font-semibold text-ink')}>
                      {feature.name}
                    </td>
                    <td className="text-center py-3 px-2">
                      <FeatureValue value={feature.good} />
                    </td>
                    <td className="text-center py-3 px-2 bg-accent/5">
                      <FeatureValue value={feature.better} />
                    </td>
                    <td className="text-center py-3 px-2">
                      <FeatureValue value={feature.best} />
                    </td>
                    <td className="text-center py-3 px-2">
                      <FeatureValue value={feature.enterprise} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SectionItem>
      </Section>

      {/* Enterprise CTA Section */}
      <Section background="ink">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <SectionItem>
            <h2 className="font-display text-h2 text-paper mb-4">
              Need a custom solution?
            </h2>
            <p className="text-gray-300 text-lg mb-6">
              Enterprise plans are tailored to your business. Get dedicated support,
              custom integrations, advanced security, and white-glove implementation.
            </p>
            <ul className="space-y-3 mb-8">
              {[
                'Custom integrations with your existing systems',
                'Dedicated success manager and priority support',
                'Advanced security and compliance (SOC 2, SSO)',
                'Custom training and onboarding for your team',
                'SLA guarantees with uptime commitments',
                'Volume discounts for large teams',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-gray-300">
                  <CheckIcon className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </SectionItem>
          <SectionItem>
            <div className="bg-paper rounded-2xl p-8 text-ink">
              <h3 className="font-heading font-bold text-xl mb-2">Contact Sales</h3>
              <p className="text-muted mb-6">
                Let's discuss how Omniforge can transform your operations.
              </p>
              <div className="space-y-4">
                <Link
                  to="/demo?plan=enterprise"
                  className="btn-primary w-full text-center block"
                  onClick={() => trackCTAClick('enterprise_schedule_demo', '/demo?plan=enterprise')}
                >
                  Schedule a Demo
                </Link>
                <Link
                  to="/contact"
                  className="btn-secondary w-full text-center block"
                  onClick={() => trackCTAClick('enterprise_talk_to_sales', '/contact')}
                >
                  Talk to Sales
                </Link>
              </div>
              <p className="text-sm text-muted text-center mt-4">
                Typical response within 24 hours
              </p>
            </div>
          </SectionItem>
        </div>
      </Section>

      {/* FAQ */}
      <Section>
        <SectionHeader title="Frequently asked questions" />
        <div className="max-w-3xl mx-auto">
          {pricingFaqs.map((faq, i) => (
            <SectionItem key={i}>
              <div className="border-b border-gray-200">
                <button
                  className="w-full py-5 flex items-center justify-between text-left"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  aria-expanded={openFaq === i}
                >
                  <span className="font-heading font-semibold pr-4">{faq.question}</span>
                  <ChevronIcon
                    className={clsx(
                      'w-5 h-5 text-muted transition-transform',
                      openFaq === i && 'rotate-180'
                    )}
                  />
                </button>
                <AnimatePresence>
                  {openFaq === i && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <p className="pb-5 text-muted">{faq.answer}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </SectionItem>
          ))}
        </div>
      </Section>

      {/* Bottom CTA */}
      <Section background="accent">
        <div className="text-center max-w-2xl mx-auto">
          <SectionItem>
            <h2 className="font-display text-h2 text-white mb-4">
              Ready to get started?
            </h2>
            <p className="text-white/80 mb-8">
              Join hundreds of service companies already running on Omniforge.
              Start your 14-day free trial today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/demo"
                className="btn-primary bg-white text-accent hover:bg-gray-100"
                onClick={() => {
                  trackCTAClick('pricing_start_trial', '/demo');
                  trackConversion('free_trial_start');
                }}
              >
                Start Free Trial
              </Link>
              <Link
                to="/contact"
                className="btn-secondary border-white text-white hover:bg-white hover:text-accent"
                onClick={() => trackCTAClick('pricing_contact_sales', '/contact')}
              >
                Contact Sales
              </Link>
            </div>
          </SectionItem>
        </div>
      </Section>
    </>
  );
}

// Enhanced Pricing Card with annual/monthly support
interface PricingCardEnhancedProps {
  name: string;
  description: string;
  price: number | 'contact';
  originalPrice: number | 'contact';
  priceUnit: string;
  features: string[];
  ctaLabel: string;
  ctaHref: string;
  highlighted?: boolean;
  isAnnual: boolean;
  className?: string;
}

function PricingCardEnhanced({
  name,
  description,
  price,
  originalPrice,
  priceUnit,
  features,
  ctaLabel,
  ctaHref,
  highlighted = false,
  isAnnual,
  className,
}: PricingCardEnhancedProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30, scale: 0.95 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className={clsx(
        'card flex flex-col relative',
        highlighted ? 'border-2 border-accent' : 'border border-gray-200',
        className
      )}
    >
      {highlighted && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-accent text-white text-sm font-semibold px-4 py-1 rounded-full">
          Most Popular
        </div>
      )}

      <div className="mb-6">
        <h3 className="font-heading font-bold text-xl mb-1">{name}</h3>
        <p className="text-muted text-sm">{description}</p>
      </div>

      <div className="mb-6">
        {price === 'contact' ? (
          <div className="text-3xl font-bold">Custom</div>
        ) : (
          <div>
            <div className="flex items-baseline gap-1">
              <span className="text-4xl font-bold">${price}</span>
              <span className="text-muted">/{priceUnit}</span>
            </div>
            {isAnnual && originalPrice !== 'contact' && (
              <div className="text-sm text-muted mt-1">
                <span className="line-through">${originalPrice}</span>
                <span className="text-success ml-2">billed annually</span>
              </div>
            )}
          </div>
        )}
      </div>

      <ul className="space-y-3 mb-8 flex-1">
        {features.map((feature, i) => (
          <li key={i} className="flex items-start gap-2">
            <CheckIcon className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
            <span className="text-sm">{feature}</span>
          </li>
        ))}
      </ul>

      <Link
        to={ctaHref}
        className={clsx(
          'w-full text-center py-3 px-6 rounded-lg font-semibold transition-all',
          highlighted ? 'btn-primary' : 'btn-secondary'
        )}
        onClick={() => {
          trackPricingSelection(name.toLowerCase(), isAnnual);
          trackCTAClick(`pricing_${name.toLowerCase()}`, ctaHref);
          trackConversion('pricing_cta_click', price === 'contact' ? undefined : price);
        }}
      >
        {ctaLabel}
      </Link>
    </motion.div>
  );
}

// Feature value display for comparison table
function FeatureValue({ value }: { value: boolean | string }) {
  if (value === true) {
    return <CheckIcon className="w-5 h-5 text-success mx-auto" />;
  }
  if (value === false) {
    return <span className="text-gray-300">-</span>;
  }
  return <span className="text-sm font-medium text-ink">{value}</span>;
}

// Icons
function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
    </svg>
  );
}

function ChevronIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
    </svg>
  );
}

function ShieldCheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  );
}
